import { login, registerClient } from '../api/auth.js';
import { setUserSession } from '../state/store.js';

let activeTab = 'signin';

export function renderAuthView(container, options = {}) {
  container.innerHTML = `
    <div class="max-w-md mx-auto my-12 bg-slate-900/90 border border-slate-800 p-6 sm:p-8 rounded-3xl shadow-2xl space-y-6">
      <div class="text-center space-y-1">
        <h2 class="text-2xl font-black text-white">Client Access Portal</h2>
        <p class="text-xs text-slate-400">Sign in to your portal account or request access.</p>
      </div>

      <!-- TABS -->
      <div class="flex bg-slate-950 p-1 rounded-2xl border border-slate-800">
        <button id="tab-signin" type="button" class="flex-1 py-2 text-xs font-bold rounded-xl transition ${
          activeTab === 'signin' ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-400 hover:text-white'
        }">
          Sign In
        </button>
        <button id="tab-signup" type="button" class="flex-1 py-2 text-xs font-bold rounded-xl transition ${
          activeTab === 'signup' ? 'bg-blue-600 text-white shadow-lg' : 'text-slate-400 hover:text-white'
        }">
          Create Account
        </button>
      </div>

      <div id="auth-alert" class="hidden p-3 rounded-xl text-xs"></div>

      <!-- SIGN IN FORM -->
      <form id="signin-form" class="${activeTab === 'signin' ? 'space-y-4' : 'hidden'}">
        <div class="space-y-1">
          <label class="text-xs font-semibold text-slate-300">Registered Email *</label>
          <input type="email" id="signinEmail" class="input-dark" placeholder="e.g. agent@example.com" required />
        </div>

        <div class="space-y-1">
          <label class="text-xs font-semibold text-slate-300">Access PIN *</label>
          <input type="password" id="signinPin" class="input-dark" placeholder="Enter your Access PIN" required />
        </div>

        <button type="submit" id="signin-btn" class="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold py-3 rounded-xl transition shadow-lg shadow-blue-600/30 text-xs">
          Sign In to Portal
        </button>
      </form>

      <!-- SIGN UP / REQUEST ACCESS FORM -->
      <form id="signup-form" class="${activeTab === 'signup' ? 'space-y-4' : 'hidden'}">
        <div class="grid grid-cols-2 gap-3">
          <div class="space-y-1">
            <label class="text-xs font-semibold text-slate-300">First Name *</label>
            <input type="text" id="signupFirstName" class="input-dark" placeholder="e.g. Alex" required />
          </div>
          <div class="space-y-1">
            <label class="text-xs font-semibold text-slate-300">Last Name</label>
            <input type="text" id="signupLastName" class="input-dark" placeholder="e.g. Smith" />
          </div>
        </div>

        <div class="space-y-1">
          <label class="text-xs font-semibold text-slate-300">Email Address *</label>
          <input type="email" id="signupEmail" class="input-dark" placeholder="client@example.co.uk" required />
        </div>

        <div class="space-y-1">
          <label class="text-xs font-semibold text-slate-300">Phone Number</label>
          <input type="tel" id="signupPhone" class="input-dark" placeholder="07700 900000" />
        </div>

        <div class="space-y-1">
          <label class="text-xs font-semibold text-slate-300">Agency / Company Name</label>
          <input type="text" id="signupCompany" class="input-dark" placeholder="e.g. Apex Property Management" />
        </div>

        <div class="space-y-1">
          <label class="text-xs font-semibold text-slate-300">Billing Address</label>
          <input type="text" id="signupAddress" class="input-dark" placeholder="e.g. 100 High Street, Manchester" />
        </div>

        <button type="submit" id="signup-btn" class="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold py-3 rounded-xl transition shadow-lg shadow-blue-600/30 text-xs">
          Submit Account Registration
        </button>
      </form>
    </div>
  `;

  const alertBox = container.querySelector('#auth-alert');
  const signinTab = container.querySelector('#tab-signin');
  const signupTab = container.querySelector('#tab-signup');
  const signinForm = container.querySelector('#signin-form');
  const signupForm = container.querySelector('#signup-form');

  signinTab.addEventListener('click', () => {
    activeTab = 'signin';
    renderAuthView(container, options);
  });

  signupTab.addEventListener('click', () => {
    activeTab = 'signup';
    renderAuthView(container, options);
  });

  // Sign In Submission
  signinForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    hideAlert(alertBox);
    const email = container.querySelector('#signinEmail').value.trim();
    const pin = container.querySelector('#signinPin').value.trim();
    const submitBtn = container.querySelector('#signin-btn');

    try {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Authenticating...';
      const user = await login(email, pin);
      setUserSession(user);
      if (typeof options.onSuccess === 'function') options.onSuccess();
    } catch (err) {
      showAlert(alertBox, 'error', err.message || 'Authentication failed.');
    } finally {
      submitBtn.disabled = false;
      submitBtn.textContent = 'Sign In to Portal';
    }
  });

  // Sign Up Submission
  signupForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    hideAlert(alertBox);
    const submitBtn = container.querySelector('#signup-btn');

    const formData = {
      firstName: container.querySelector('#signupFirstName').value.trim(),
      lastName: container.querySelector('#signupLastName').value.trim(),
      email: container.querySelector('#signupEmail').value.trim(),
      phone: container.querySelector('#signupPhone').value.trim(),
      companyName: container.querySelector('#signupCompany').value.trim(),
      billingAddress: container.querySelector('#signupAddress').value.trim()
    };

    try {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Registering Account...';
      await registerClient(formData);
      showAlert(alertBox, 'success', 'Registration submitted! Please check your email for your Access PIN.');
      signupForm.reset();
    } catch (err) {
      showAlert(alertBox, 'error', err.message || 'Registration failed.');
    } finally {
      submitBtn.disabled = false;
      submitBtn.textContent = 'Submit Account Registration';
    }
  });
}

function showAlert(element, type, message) {
  element.className = `p-3 rounded-xl text-xs ${
    type === 'success'
      ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30'
      : 'bg-red-500/10 text-red-400 border border-red-500/30'
  }`;
  element.textContent = message;
  element.classList.remove('hidden');
}

function hideAlert(element) {
  element.className = 'hidden p-3 rounded-xl text-xs';
  element.textContent = '';
}